import { useEffect, useState } from 'react';
import { addDays, EXAMPLE_FACTORY_FULL_TIME, fmtDate, fmtDateTime, ruleConfigSchema, type RuleConfig, type RuleTemplateDTO } from '@em/shared';
import { api, qk, queryClient, useRuleTemplates, useSettings } from '../../api';
import { todayIn } from '../../components/format';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Field, Input, PageHeader, QueryState, Textarea } from '../../components/ui';
import { RuleConfigForm } from './RuleConfigForm';
import { RuleTester } from './RuleTester';

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export function RulesPage() {
  const q = useRuleTemplates();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ template?: RuleTemplateDTO; config: RuleConfig } | null>(null);
  const [viewing, setViewing] = useState<{ title: string; config: RuleConfig } | null>(null);

  useEffect(() => {
    if (!selectedId && q.data?.[0]) setSelectedId(q.data[0].id);
  }, [q.data, selectedId]);
  const selected = q.data?.find((t) => t.id === selectedId) ?? null;
  const current = selected?.versions[0];

  return (
    <>
      <PageHeader
        title="Pay rules"
        subtitle="Rule templates turn punches into payroll categories. Each change is a new dated version; past versions stay attached to the hours they produced."
        actions={
          <Button variant="primary" onClick={() => setEditor({ config: clone(EXAMPLE_FACTORY_FULL_TIME) })}>
            New template
          </Button>
        }
      />
      <Alert tone="amber">Example values are modelled on common award clauses. Confirm every value with your payroll adviser before relying on it.</Alert>
      <QueryState q={q} loadingLabel="Loading rule templates">
        {(templates) =>
          templates.length === 0 ? (
            <Card className="section">
              <EmptyState title="No rule templates yet" action={<Button onClick={() => setEditor({ config: clone(EXAMPLE_FACTORY_FULL_TIME) })}>Create one from the example</Button>} />
            </Card>
          ) : (
            <div className="grid section" style={{ gridTemplateColumns: 'minmax(220px, 1fr) minmax(0, 3fr)' }}>
              <Card title="Templates" tight>
                <table className="table">
                  <tbody>
                    {templates.map((t) => (
                      <tr key={t.id} style={t.id === selectedId ? { background: 'var(--accent-soft)' } : undefined}>
                        <td>
                          <button className="btn ghost" style={{ height: 'auto', padding: 0, fontWeight: 600 }} onClick={() => setSelectedId(t.id)} aria-pressed={t.id === selectedId}>
                            {t.name}
                          </button>
                          <div className="small muted">
                            v{t.versions[0]?.versionNumber ?? 0} · {t.assignedEmployeeCount} employee{t.assignedEmployeeCount === 1 ? '' : 's'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              {selected && (
                <Card
                  title={selected.name}
                  tight
                  actions={
                    current && (
                      <Button variant="primary" size="sm" onClick={() => setEditor({ template: selected, config: clone(current.config) })}>
                        New version from a date
                      </Button>
                    )
                  }
                >
                  {selected.description && <div className="card-body">{selected.description}</div>}
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Effective from</th>
                        <th>Effective to</th>
                        <th>Created</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {selected.versions.map((v) => (
                        <tr key={v.id}>
                          <td className="strong">
                            v{v.versionNumber} {!v.effectiveTo && <Badge tone="green">Current</Badge>}
                          </td>
                          <td className="nowrap">{fmtDate(v.effectiveFrom)}</td>
                          <td className="nowrap">{v.effectiveTo ? fmtDate(v.effectiveTo) : <span className="muted">Open</span>}</td>
                          <td className="small">
                            {v.createdByName ?? 'System'} · {fmtDateTime(v.createdAt, 'Australia/Sydney')}
                          </td>
                          <td className="actions">
                            <Button size="sm" onClick={() => setViewing({ title: `${selected.name} v${v.versionNumber}`, config: v.config })}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </div>
          )
        }
      </QueryState>

      <RuleTester templates={q.data ?? []} defaultTemplateId={selectedId} />

      {editor && <RuleEditor template={editor.template} initial={editor.config} onClose={() => setEditor(null)} onSaved={(id) => setSelectedId(id)} />}
      {viewing && (
        <Modal title={viewing.title} wide onClose={() => setViewing(null)} footer={<Button onClick={() => setViewing(null)}>Close</Button>}>
          <pre className="json" style={{ maxHeight: '60vh' }}>
            {JSON.stringify(viewing.config, null, 2)}
          </pre>
        </Modal>
      )}
    </>
  );
}

function RuleEditor({ template, initial, onClose, onSaved }: { template?: RuleTemplateDTO; initial: RuleConfig; onClose: () => void; onSaved: (id: string) => void }) {
  const settings = useSettings();
  const toast = useToast();
  const latest = template?.versions[0];
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [from, setFrom] = useState(() => {
    const t = todayIn(settings.data?.defaultTimezone ?? 'Australia/Sydney');
    return latest && latest.effectiveFrom >= t ? addDays(latest.effectiveFrom, 1) : t;
  });
  const [config, setConfig] = useState<RuleConfig>(initial);
  const [issues, setIssues] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setError(null);
    const parsed = ruleConfigSchema.safeParse(config);
    if (!parsed.success) {
      setIssues(parsed.error.issues.map((i) => `${i.path.join(' › ') || 'Rules'}: ${i.message}`));
      return;
    }
    setIssues([]);
    setBusy(true);
    try {
      const t = template
        ? await api.createRuleVersion(template.id, { config: parsed.data, effectiveFrom: from })
        : await api.createRuleTemplate({ name: name.trim(), description: description.trim() || null, config: parsed.data, effectiveFrom: from });
      await queryClient.invalidateQueries({ queryKey: qk.ruleTemplates });
      for (const k of ['timesheet', 'pay-period', 'exceptions']) void queryClient.invalidateQueries({ queryKey: [k] });
      toast.success(template ? `Version ${t.versions[0]?.versionNumber} saved` : 'Template created', template ? 'Open timesheets from that date have been recalculated.' : undefined);
      onSaved(t.id);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      wide
      title={template ? `New version of ${template.name}` : 'New rule template'}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!from || (!template && !name.trim())}>
            {template ? 'Save new version' : 'Create template'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        {issues.length > 0 && (
          <Alert tone="red" title="Fix these before saving">
            <ul>
              {issues.map((i, k) => (
                <li key={k}>{i}</li>
              ))}
            </ul>
          </Alert>
        )}
        <div className="form-grid">
          {!template && (
            <>
              <Field label="Name" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </Field>
              <Field label="Description">
                <Textarea rows={1} style={{ minHeight: 34 }} value={description} onChange={(e) => setDescription(e.target.value)} />
              </Field>
            </>
          )}
          <Field
            label="Effective from"
            required
            hint={latest ? `Must be after ${fmtDate(latest.effectiveFrom)} (start of v${latest.versionNumber}) and in an open pay period. The current version ends the day before.` : 'First day these rules apply'}
          >
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          </Field>
        </div>
        <RuleConfigForm value={config} onChange={setConfig} />
      </div>
    </Modal>
  );
}
